import { OrbitControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import React, { FunctionComponent, useRef, useEffect, useState } from 'react';
import { Box3, MathUtils, Object3D, Object3DEventMap, PerspectiveCamera, Spherical, Vector3 } from 'three';
import { OrbitControls as OrbitControlsImpl } from 'three-stdlib';
import { IMode, useDataStore, useSceneStore } from '../zustand';
import { IRoot, ISceneComp, ITrackingTypes } from '@r3f-component-data-structure';
import { Jute } from '..';
import { initialiseWebEmbed } from '@r3f-component-data-structure/types/webEmbed';
import { fitCameraToObjects } from '@r3f/utils/fitCameraToObjects';
import { panCameraAcrossView } from '@r3f/utils/panCameraAcrossView';
import { maxPolarAboveSurface, surfaceOvershoot } from '@r3f/utils/roomBounds';
import { useGetActiveTrackingType } from './hoc/hocCustomHooks';

const PREVIEW_ORBIT_CAMERA_NAME = 'preview-orbit-camera';

// const MAX_WOBBLE_COUNT = 1;
// const WOBBLE_RADIANS = MathUtils.degToRad(8);
// const WOBBLE_SPEED_FACTOR = 0.9;
// const ROTATION_MATRIX = new Matrix4();

const ZOOM_VARIATION_PERCENT = 25;

/**
 * Orbit limits for the preview simulator, matching Designer's synthetic-camera room so the two
 * previews feel like one tool. Free in every direction the room allows, which is everywhere except
 * through its own floor — see the rotation limit below. The epsilons keep the camera off the poles,
 * where a look-at has no up vector to work from and the view flips.
 */
const PREVIEW_MIN_DISTANCE = 0.001;
const PREVIEW_POLAR_EPSILON = 0.001;

/**
 * Where `PreviewRoom` puts its plank floor, in scene units.
 *
 * It passes no `floorStandoffM`, so this is `RoomSurfaces`' own default at the room's 1 unit per
 * metre. Stated rather than imported because the floor is the room's decision and this is the camera's
 * reading of it; if the two ever disagree the camera stops a centimetre out, not in another room.
 */
const PREVIEW_FLOOR_Y = -0.01;

/**
 * Where to stand when the active scene cannot be measured — see the degenerate-box note below.
 *
 * Metres, because the preview runs in AR mode where a scene unit IS a metre: looking at a point 0.75m
 * above the anchor from 4m back and 1.8m up. Roughly where you would stand to look at something placed
 * on the floor in front of you.
 */
const PREVIEW_FALLBACK_TARGET: [number, number, number] = [0, 0.75, 0];
const PREVIEW_FALLBACK_POSITION: [number, number, number] = [0, 1.8, 4];

/**
 * How large the active scene must measure, in metres, before it is worth framing.
 *
 * A scene whose content has not loaded measures as an empty box — but so does one whose content is
 * entirely SCREEN-RELATIVE, which renders in its own overlay scene and so leaves the AR side
 * legitimately empty forever. Framing either parks the camera on its own target, where orbiting
 * rotates a zero-radius sphere and the preview looks frozen.
 */
const PREVIEW_MIN_FRAMED_SIZE = 0.05;

/** How often to re-check whether the active scene can be measured yet, and when to stop asking. */
const PREVIEW_FRAMING_RETRY_MS = 250;
const PREVIEW_FRAMING_TIMEOUT_MS = 15000;

/** A walk is measured against the view at the target, so a camera sitting on its own target still moves. */
const MIN_WALK_DISTANCE = 0.01;

interface IPreviewView {
	position: Vector3;
	target: Vector3;
	/**
	 * Where the content's centre was when this view was recorded.
	 *
	 * Kept BESIDE the target rather than assumed equal to it. Placement runs again on every
	 * re-activation and the anchor can land somewhere else, so a restored view has to be shifted by
	 * however far the content moved — and with panning available the target is no longer the content's
	 * centre, so it can no longer stand in for it. Deriving the shift from the target instead is what
	 * would silently undo a user's pan on every revisit.
	 */
	centre: Vector3;
	near: number;
	far: number;
}

/**
 * Where the camera was left, per scene, for the life of the page.
 *
 * MODULE scope, not a ref: `ZapparCanvas` only mounts this component while the active scene is a
 * world one, so any per-component state is wiped by a trip through an image or face scene and the
 * camera would re-frame on the way back.
 *
 * Restoring the TARGET matters as much as the position. drei rebuilds OrbitControls whenever the
 * default camera swaps, which resets the target to the origin — so a scene that merely skipped
 * re-framing would keep the user's camera but orbit around the origin instead of the content.
 */
const previewViewsBySceneId = new Map<string, IPreviewView>();

const OrbitControlsWrapper: FunctionComponent = () => {
	const [focused, setFocused] = useState(false);
	const { camera, scene: threeScene, gl } = useThree();
	const orbitControlsRef = useRef<OrbitControlsImpl>(null);
	const componentsById = useDataStore(({ componentsById }) => componentsById);
	const mode = useDataStore(({ mode }) => mode);
	const rootId = useDataStore(({ rootId }) => rootId);
	const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId);
	const activeTrackingType = useGetActiveTrackingType();
	const { cameraPosition, orbitTarget, enableZoom, enablePan, dampingFactor, autoRotateSpeed, verticalOrbit, horizontalOrbit, hintGraphic } = useDataStore(
		({ webEmbed }) => webEmbed) || initialiseWebEmbed();

	// const isWobblingRef = useRef(false);
	// const wobbleCountRef = useRef<number>(0);
	// const oldAngleRef = useRef(0);

	/**
	 * The preview orbits a world scene while the experience stays in AR mode, so every gate below that
	 * used to mean "non-AR" now means "non-AR, or this one preview scene".
	 */
	const isWorldPreviewScene = Jute.isPreviewSimulator && activeTrackingType === ITrackingTypes.world;
	const orbits = mode === IMode.nonar || isWorldPreviewScene;

	// For the camera-control handle below, which is registered once on mount and so cannot close over it.
	const worldPreviewSceneRef = useRef(isWorldPreviewScene);
	worldPreviewSceneRef.current = isWorldPreviewScene;

	/**
	 * Which scene the user has taken the camera over on, so a content update landing mid-drag cannot
	 * restart the retry poll and frame out from under them. The local `let` this replaced was rebuilt
	 * as `false` on every effect run, and a plain boolean would leak across scenes and leave the next
	 * one never framed.
	 */
	const userHasTakenOverSceneRef = useRef<string | null>(null);

	/**
	 * Which scene the camera has already been settled for, and on WHICH controls instance. Settling must
	 * happen once per activation, not once per effect run: the effect re-runs on every content update,
	 * and re-applying even the REMEMBERED view would snap the camera back to its last settled pose
	 * mid-drag — the same fault as re-framing, just less obvious.
	 *
	 * The controls half is what the re-entry bug turned on: drei rebuilds OrbitControls a commit after
	 * the default camera swaps, so a scene marked settled by the pre-swap run left the live instance
	 * orbiting the origin. See docs/PREVIEW_OVERVIEW.md §10.
	 */
	const cameraSettledRef = useRef<{ sceneId: string; controls: OrbitControlsImpl } | null>(null);

	useEffect(() => {
		if (!orbits || !componentsById || !rootId || !orbitControlsRef.current) return;

		const firstSceneId = (componentsById?.[rootId] as IRoot)?.children[0];
		const activeSceneIsFaceTracking = (componentsById?.[activeSceneId || firstSceneId] as ISceneComp)?.trackingType === ITrackingTypes.face;
		const activeSceneIsNoTrackingScreen = (componentsById?.[activeSceneId || firstSceneId] as ISceneComp)?.trackingType === ITrackingTypes.noTrackingScreen;

		// The preview simulator ignores every webEmbed setting. Those exist to keep an embedded viewer
		// on rails inside someone's page; here the whole point is to walk around the content freely,
		// and a project that happens to carry embed settings must not silently lock the preview.
		if (isWorldPreviewScene) {
			const controls = orbitControlsRef.current;

			// Resolve the orbit camera BY IDENTITY, never from `useThree().camera`. The default camera
			// swaps between this one and the Zappar camera as scenes change, and this effect can run
			// before that swap commits. On a Zappar camera `near`/`far` are setter aliases for
			// `zNear`/`zFar` — the inputs to its per-frame projection — so a stray write there produces
			// a permanently NaN projection and every tracked scene renders nothing.
			const previewCamera = threeScene.getObjectByName(PREVIEW_ORBIT_CAMERA_NAME) as PerspectiveCamera | undefined;
			if (!previewCamera) return;

			// drei builds OrbitControls around whatever `useThree().camera` was at RENDER time, and the
			// preview camera only becomes default in a layout effect one commit later — so the first run
			// after a non-world scene holds an instance drei is about to throw away. Touching it is
			// wasted, and marking the scene settled from it is what left the live instance orbiting the
			// origin. The `camera` dependency brings this effect back on the commit that has the real one.
			if (controls.object !== previewCamera) return;

			const applyPreviewLimits = () => {
				// After any fit, which writes maxDistance itself. `maxPolarAngle` is re-derived every
				// frame from the floor — this only sets the ceiling that rule narrows.
				controls.minDistance = PREVIEW_MIN_DISTANCE;
				controls.maxDistance = Infinity;
				controls.minPolarAngle = PREVIEW_POLAR_EPSILON;
				controls.maxPolarAngle = Math.PI - PREVIEW_POLAR_EPSILON;
				controls.minAzimuthAngle = -Infinity;
				controls.maxAzimuthAngle = Infinity;
				controls.update();
				// So the shell's reset button comes back to this framing rather than wherever
				// OrbitControls happened to be constructed.
				controls.saveState();
			};

			const sceneId = activeSceneId || firstSceneId;

			/**
			 * Remember where the camera ended up, so revisiting this scene restores it rather than re-framing.
			 *
			 * `centre` is carried forward from whatever is already on record unless a caller that has just
			 * measured the content passes a fresh one. Re-measuring here instead would put a `setFromObject`
			 * over the whole scene graph on the settle of every drag and every keyboard nudge.
			 */
			const rememberView = (centre?: Vector3) => {
				const known = centre ?? previewViewsBySceneId.get(sceneId)?.centre;
				previewViewsBySceneId.set(sceneId, {
					position: previewCamera.position.clone(),
					target: controls.target.clone(),
					centre: known ? known.clone() : new Vector3(...PREVIEW_FALLBACK_TARGET),
					near: previewCamera.near,
					far: previewCamera.far
				});
			};

			// Once someone is looking around, their view is the one to keep — so record it and never
			// frame this scene, rather than waiting for a fit that would yank the camera out from under them.
			const onUserInput = () => { userHasTakenOverSceneRef.current = sceneId; };
			const onSettle = () => rememberView();
			controls.addEventListener('start', onUserInput);
			controls.addEventListener('end', onSettle);

			const detachListeners = () => {
				controls.removeEventListener('start', onUserInput);
				controls.removeEventListener('end', onSettle);
			};

			const userHasTakenOver = () => userHasTakenOverSceneRef.current === sceneId;

			// The camera is already where it should be for this scene, and this run is just another
			// content update. Keep listening so the remembered view stays current, and touch nothing.
			if (cameraSettledRef.current?.sceneId === sceneId && cameraSettledRef.current.controls === controls) {
				return detachListeners;
			}

			/**
			 * Eye height, a few metres back, looking slightly down at the anchor.
			 *
			 * The only thing that gives this camera a viewing DIRECTION, and `fitCameraToObjects` never
			 * picks one — so it has to be established BEFORE the first fit, not after one fails. Applied
			 * afterwards it only ever worked on a cold boot, where nothing is measurable yet. See
			 * docs/PREVIEW_OVERVIEW.md §10.
			 */
			const applyStandingPose = () => {
				controls.target.set(...PREVIEW_FALLBACK_TARGET);
				previewCamera.position.set(...PREVIEW_FALLBACK_POSITION);
				previewCamera.near = 0.01;
				previewCamera.far = 1000;
				previewCamera.updateProjectionMatrix();
				applyPreviewLimits();
			};

			/** The active scene and its bounds, or null while it is too small to frame — see PREVIEW_MIN_FRAMED_SIZE. */
			const measureActiveScene = (): { object: Object3D<Object3DEventMap>; box: Box3 } | null => {
				const activeThreeScene = threeScene.getObjectByName(sceneId) as Object3D<Object3DEventMap>;
				if (!activeThreeScene) return null;
				// Measure BEFORE fitting. `fitCameraToObjects` moves the camera as a side effect, so
				// probing with it and rejecting the result afterwards still leaves the camera wherever
				// the failed fit put it — which the retry below then repeated every 250ms, wiping out
				// the fallback pose each time and pinning the camera to its own target.
				const box = new Box3().setFromObject(activeThreeScene);
				const size = box.getSize(new Vector3());
				if (Math.max(size.x, size.y, size.z) < PREVIEW_MIN_FRAMED_SIZE) return null;
				return { object: activeThreeScene, box };
			};

			/**
			 * Put the camera back where the user left it on this scene.
			 *
			 * Re-centred on the content as it stands now. The anchor no longer moves between activations
			 * — a placed scene stays placed — but a live edit can still resize or reposition the content
			 * while the scene is away, so the shift is still worth measuring. It is measured against the
			 * CENTRE recorded with the view, not against its target: the two used to be the same point
			 * because pan was off, and now that a pan can move the target away from the content, using
			 * the target would quietly undo that pan every time the scene came back.
			 */
			const restoreRememberedView = (savedView: IPreviewView): boolean => {
				const measured = measureActiveScene();
				const centre = measured?.box.getCenter(new Vector3());
				const shift = centre ? centre.clone().sub(savedView.centre) : new Vector3();
				previewCamera.position.copy(savedView.position).add(shift);
				previewCamera.near = savedView.near;
				previewCamera.far = savedView.far;
				previewCamera.updateProjectionMatrix();
				controls.target.copy(savedView.target).add(shift);
				applyPreviewLimits();
				rememberView(centre ?? savedView.centre);
				return true;
			};

			/**
			 * Frame the active scene, or report that it cannot be measured yet.
			 *
			 * A scene whose models have not loaded measures as a zero-size box, and fitCameraToObjects
			 * then parks the camera exactly ON its target: radius zero, so orbiting and zooming move
			 * nothing and the preview looks frozen.
			 */
			const frameActiveScene = (): boolean => {
				const measured = measureActiveScene();
				if (!measured) return false;

				// Centre the target BEFORE fitting. `fitCameraToObjects` derives the viewing direction from
				// the target it finds and only then recentres it, so a target that disagrees with the
				// content's centre turns a re-fit into a rotation — and every revisit compounds it.
				const centre = measured.box.getCenter(new Vector3());
				controls.target.copy(centre);

				// The standing pose, stated rather than inherited. `fitCameraToObjects` derives its
				// viewing direction from this point towards the target, so naming it here fixes the
				// opening angle of every world scene instead of leaving it to wherever the camera
				// happened to be. Safe because framing only ever runs on a fresh activation — a revisit
				// goes through `restoreRememberedView`, which does not fit and so keeps the user's angle.
				fitCameraToObjects(previewCamera, controls, [measured.object], 1.2, PREVIEW_FALLBACK_POSITION);
				applyPreviewLimits();
				rememberView(centre);
				return true;
			};

			/**
			 * Settle the camera for this scene, if it can be settled yet.
			 *
			 * PLACEMENT OWNS THE CAMERA. While the user is placing, the camera's ray onto the floor IS the
			 * anchor, so the standing fallback pose below is the right one — a remembered viewing pose is
			 * by construction tight on the content and can leave the ray off the floor entirely, which
			 * reads as tracking having failed. The saved view comes back the moment placement ends.
			 * Read through `getState` rather than a hook: as a dependency it would re-run this effect.
			 *
			 * And placement has not even been OFFERED until the world tracker is enabled, which lags
			 * `activeSceneId` by the whole of the outgoing scene's exit transition (`TrackerGroups` flips
			 * it at `exitFinish`). Settling in that window framed the content tight and marked the scene
			 * settled, so the standing pose never returned and the placement ray never found the floor.
			 */
			const settleCamera = (): boolean => {
				const { placementMode, enabledTrackingType } = useSceneStore.getState();
				if (placementMode || enabledTrackingType !== ITrackingTypes.world) return false;
				const savedView = previewViewsBySceneId.get(sceneId);
				const settled = savedView ? restoreRememberedView(savedView) : frameActiveScene();
				// Only now do later content updates leave the camera alone. Deliberately NOT set while
				// polling: the retry loop is torn down and rebuilt by each effect re-run, and marking it
				// early would end the retries with the camera still on the fallback pose.
				if (settled) cameraSettledRef.current = { sceneId, controls };
				return settled;
			};

			// Checked before anything writes to the camera: someone already looking around owns it, and
			// an effect re-run mid-drag must not snap them back.
			if (userHasTakenOver()) return detachListeners;

			// A fresh activation of this scene, so seed the direction before anything frames with it. A
			// revisit is left alone — `restoreRememberedView` carries the user's own angle back.
			if (!previewViewsBySceneId.has(sceneId)) applyStandingPose();

			if (settleCamera()) return detachListeners;

			// Stand somewhere sensible in the meantime. fitCameraToObjects derived near/far from the
			// degenerate distance too, leaving both at 0.
			applyStandingPose();

			// This effect runs on mount, long before assets land, so the degenerate box is the COMMON
			// case rather than the edge one — and a fallback that never re-frames leaves the camera
			// orbiting a fixed point while the content sits off to one side, drifting out of shot.
			// So keep trying until the scene can be settled, and stop the moment the user takes over:
			// re-framing under someone who is already looking around is worse than never framing at all.
			//
			// The deadline is a budget for content that has not loaded, not for a user who has not
			// pressed Place, nor for a tracker that has not been handed over yet — so both push it
			// forward rather than spending it.
			let framingDeadline = Date.now() + PREVIEW_FRAMING_TIMEOUT_MS;
			const poll = setInterval(() => {
				const { placementMode, enabledTrackingType } = useSceneStore.getState();
				if (placementMode || enabledTrackingType !== ITrackingTypes.world) {
					framingDeadline = Date.now() + PREVIEW_FRAMING_TIMEOUT_MS;
					return;
				}
				if (userHasTakenOver() || settleCamera() || Date.now() > framingDeadline) stopPolling();
			}, PREVIEW_FRAMING_RETRY_MS);

			function stopPolling() {
				clearInterval(poll);
			}

			return () => {
				stopPolling();
				detachListeners();
			};
		}

		if (!cameraPosition && !orbitTarget && !activeSceneIsFaceTracking && !activeSceneIsNoTrackingScreen) {
			// if no initial camera position and orbit target zoom camera to fit all objects of active scene
			const activeThreeScene = threeScene.getObjectByName(activeSceneId || firstSceneId) as Object3D<Object3DEventMap>;
			fitCameraToObjects(camera as PerspectiveCamera, orbitControlsRef.current, [activeThreeScene], 1.2);
		} else if (!cameraPosition && !orbitTarget && activeSceneIsFaceTracking) {
			camera.position.set(0, 0, 3) 
			orbitControlsRef.current.target.set(0, 0, 0);
			camera.updateProjectionMatrix();
		} else {
			// otherwise set custom initial orbit target and camera position
			if (orbitTarget) orbitControlsRef.current.target.set(...orbitTarget);
			if (cameraPosition) camera.position.set(...cameraPosition);
			camera.updateProjectionMatrix();
			orbitControlsRef.current.update();
		}

		if (enableZoom && activeTrackingType !== ITrackingTypes.content360) {
			const zoomVariation = ZOOM_VARIATION_PERCENT / 100;
			const initialCameraDistance = camera.position.distanceTo(new Vector3(...(orbitTarget || [0, 0, 0])));
			orbitControlsRef.current.minDistance = initialCameraDistance - initialCameraDistance * zoomVariation;
			orbitControlsRef.current.maxDistance = initialCameraDistance + initialCameraDistance * zoomVariation;
		}

		/*
		 ** If vertical orbit or horizontal orbit is set we need to get the current polar / azimuth angle
		 ** and use this as both the min and max to lock the respective angle to the current value
		 ** otherwise it resets camera to ( for example ) zero
		 */

		if (!verticalOrbit) {
			const polarAngle = orbitControlsRef?.current?.getPolarAngle() || 0;
			orbitControlsRef.current.minPolarAngle = polarAngle;
			orbitControlsRef.current.maxPolarAngle = polarAngle;
		}

		if (!horizontalOrbit && !autoRotateSpeed) {
			const azimuthAngle = orbitControlsRef?.current?.getAzimuthalAngle() || 0;
			orbitControlsRef.current.minAzimuthAngle = azimuthAngle;
			orbitControlsRef.current.maxAzimuthAngle = azimuthAngle;
		}

		orbitControlsRef.current.update();
	}, [activeSceneId, componentsById, rootId, camera, threeScene, mode, orbits, cameraPosition, orbitTarget, enableZoom, horizontalOrbit, verticalOrbit, autoRotateSpeed]);

	/**
	 * Hold the camera above the room's floor, as a ROTATION limit and nothing else.
	 *
	 * `maxPolarAngle` stops the orbit a few degrees short of the floor, and OrbitControls enforces that
	 * itself — through its own pointer drags and its damping tail — so nothing here ever has to move a
	 * camera that has already gone too far. It is re-derived every frame rather than set once because
	 * zooming changes the radius and panning or walking moves the target, and the angle at which the
	 * orbit meets the floor moves with both; a zoom that would drop the camera through the floor tightens
	 * this instead, and the next `update()` swings the camera back up to it.
	 *
	 * Correcting the camera's HEIGHT here as well — the obvious belt to this braces — is a trap, and was
	 * the first thing this did. Such a correction has to move the target with the camera, or it is not a
	 * pan but a change of orbit; but the target's height is an input to the limit above, and raising it
	 * LOOSENS that limit. So every correction licensed the next one, and holding a drag at the floor
	 * walked the whole view upwards. A pan is the only thing that can translate through the floor, and it
	 * is bounded where it is applied.
	 *
	 * Runs at the default priority, so it lands after drei's `controls.update()` at -1.
	 */
	useFrame(() => {
		if (!isWorldPreviewScene) return;
		const controls = orbitControlsRef.current;
		const previewCamera = controls?.object as PerspectiveCamera | undefined;
		if (!controls || !previewCamera) return;

		controls.maxPolarAngle = maxPolarAboveSurface(
			controls.target.y,
			PREVIEW_FLOOR_Y,
			previewCamera.position.distanceTo(controls.target),
			Math.PI - PREVIEW_POLAR_EPSILON
		);
	});

	// Positioned by hand rather than through OrbitControls' own rotate/dolly helpers: in three-stdlib
	// those are closures inside the constructor, not instance methods, so calling them throws. Spherical
	// maths against `controls.target` needs no private API and survives a three upgrade.
	//
	// Both the controls AND the camera are read off the instance at call time. `useThree().camera` is
	// whatever was default when this effect last ran, which is NOT reliably the camera OrbitControls
	// ended up driving — closing over it moved a camera that was no longer in the scene, so every
	// button silently did nothing. `controls.object` is by definition the one being driven.
	useEffect(() => {
		if (!Jute.isPreviewSimulator) return;

		// Moving the camera from here is a user taking it over, exactly as a drag is — so say so on the
		// controls' own events rather than reaching for the effect above's refs. That effect's `start`
		// and `end` listeners are what stop a pending fit re-framing out from under someone steering
		// with the keyboard, and what remember the view they left. A pointer fires the pair per gesture;
		// there is no gesture here to bracket, so each nudge is its own.
		const withControls = (fn: (c: OrbitControlsImpl, cam: PerspectiveCamera) => void) => () => {
			const c = orbitControlsRef.current;
			if (!c?.object) return;
			c.dispatchEvent({ type: 'start', target: c });
			fn(c, c.object as PerspectiveCamera);
			c.dispatchEvent({ type: 'end', target: c });
		};

		Jute.registerPreviewCameraControls({
			orbit: (deltaAzimuth, deltaPolar) =>
				withControls((c, cam) => {
					const spherical = new Spherical().setFromVector3(cam.position.clone().sub(c.target));
					spherical.theta = MathUtils.clamp(spherical.theta - deltaAzimuth, c.minAzimuthAngle, c.maxAzimuthAngle);
					spherical.phi = MathUtils.clamp(spherical.phi - deltaPolar, c.minPolarAngle, c.maxPolarAngle);
					spherical.makeSafe();
					cam.position.copy(c.target).add(new Vector3().setFromSpherical(spherical));
					cam.lookAt(c.target);
					c.update();
				})(),
			pan: (deltaRight, deltaUp) =>
				withControls((c, cam) => {
					panCameraAcrossView(cam, c.target, deltaRight, deltaUp);
					// The one place a translation can cross the floor, so the one place it is bounded.
					// Read off the ref rather than closed over: this handle is registered once, on mount.
					if (worldPreviewSceneRef.current) {
						const lift = surfaceOvershoot(cam.position.y, PREVIEW_FLOOR_Y);
						if (lift > 0) {
							cam.position.y += lift;
							c.target.y += lift;
						}
					}
					c.update();
				})(),
			walk: (deltaForward) =>
				withControls((c, cam) => {
					const distance = Math.max(cam.position.distanceTo(c.target), MIN_WALK_DISTANCE);
					const visibleHeight = 2 * distance * Math.tan(MathUtils.degToRad(cam.fov * 0.5));
					// Flattened onto the ground, so a camera pitched down walks across the scene rather
					// than into its floor. From the quaternion rather than `getWorldDirection`, which
					// reads a matrix `lookAt` leaves stale — the same trap `panCameraAcrossView` documents.
					const forward = new Vector3(0, 0, -1).applyQuaternion(cam.quaternion);
					forward.y = 0;
					// Looking straight up or down leaves no ground direction to walk along.
					if (forward.lengthSq() < 1e-8) return;
					forward.normalize().multiplyScalar(deltaForward * visibleHeight);
					// The target travels too, or this would be a zoom that stops dead at the pivot.
					cam.position.add(forward);
					c.target.add(forward);
					c.update();
				})(),
			zoom: (factor) =>
				withControls((c, cam) => {
					const offset = cam.position.clone().sub(c.target);
					offset.setLength(MathUtils.clamp(offset.length() * factor, c.minDistance, c.maxDistance));
					cam.position.copy(c.target).add(offset);
					cam.lookAt(c.target);
					c.update();
				})(),
			reset: withControls((c) => c.reset())
		});

		return () => Jute.registerPreviewCameraControls(undefined);
	}, []);

	useEffect(() => {
		// The focus dance exists so an embed does not swallow the host page's scroll until the viewer
		// deliberately clicks in. The preview owns its whole window, so it just gets in the way.
		if (Jute.isPreviewSimulator) return;

		// isWobblingRef.current = true;
		const body = document.getElementsByTagName('body')[0];
		if (hintGraphic) Jute.showWebembedHintGraphic(true, document);

		const focusHandler = () => {
			Jute.showWebembedHintGraphic(false, document);
			setFocused(true);
		};

		const blurHandler = (e: FocusEvent) => {
			if ((e as any)?.relatedTarget?.id === 'designer-embed-ar-button' || (e as any)?.relatedTarget?.id === 'designer-embed-ar-button-span') return;
			//if (hintGraphic) Jute.showWebembedHintGraphic(true, document)
			setFocused(false);
		};

		gl.domElement.addEventListener('focus', focusHandler);

		body.setAttribute('tabindex', '1');
		body.addEventListener('focus', focusHandler);
		body.addEventListener('blur', blurHandler);

		return () => {
			body.removeEventListener('focus', focusHandler);
			gl.domElement.removeEventListener('focus', focusHandler);
			body.removeEventListener('blur', blurHandler);
		};
	}, [hintGraphic]);

	// useFrame(({clock}) => {
	//     if (!isWobblingRef.current || pointerDownRef.current) return;
	//     const wobbleAngle = Math.sin( clock.elapsedTime * WOBBLE_SPEED_FACTOR );
	//     const deltaAngle = oldAngleRef.current - wobbleAngle;
	//     if ( oldAngleRef.current < 0 && wobbleAngle > 0 ) {
	//         wobbleCountRef.current = wobbleCountRef.current + 1
	//     }
	//     if (
	//         oldAngleRef.current < 0 &&
	//         wobbleAngle < 0 &&
	//         oldAngleRef.current < wobbleAngle &&
	//         wobbleCountRef.current === MAX_WOBBLE_COUNT
	//     ) {
	//         isWobblingRef.current = false;
	//         return;
	//     }
	//     ROTATION_MATRIX.makeRotationY(deltaAngle * WOBBLE_RADIANS);
	//     orbitCamera.applyMatrix4(ROTATION_MATRIX)
	//     oldAngleRef.current = wobbleAngle;
	// })

	if (Jute.isPreviewSimulator) {
		// `enablePan` stays off, and panning still works: it is driven through `Jute.previewPan` by the
		// shell, exactly as in Designer's synthetic-camera room, so the two kinds of scene pan at one
		// rate from one implementation rather than at OrbitControls' rate on one and ours on the other.
		// Leaving OrbitControls' own pan off also keeps the right button ours to read. Everything else
		// is unbounded, set in the effect above.
		return (
			<OrbitControls
				ref={orbitControlsRef}
				enabled={orbits}
				enableZoom={activeTrackingType !== ITrackingTypes.content360}
				enablePan={false}
				enableDamping
			/>
		);
	}

	return (
		<OrbitControls
			ref={orbitControlsRef}
			enabled={orbits}
			enableZoom={focused && enableZoom && activeTrackingType !== ITrackingTypes.content360}
			enablePan={focused && enablePan}
			enableDamping={!!dampingFactor}
			dampingFactor={dampingFactor}
			autoRotate={!!autoRotateSpeed}
			autoRotateSpeed={autoRotateSpeed}
		/>
	);
};

OrbitControlsWrapper.displayName = 'OrbitControlsWrapper';
export default React.memo(OrbitControlsWrapper);
